import { useState, useCallback, useEffect, useMemo } from "react"
import { Menu, MenuProps } from "antd"
import React from "react"
import { createPortal } from "react-dom"

/**
 * Position coordinates for context menu
 */
export interface ContextMenuPosition {
  x: number
  y: number
}

/**
 * Configuration options for useContextMenu hook
 */
export interface UseContextMenuOptions {
  /** Array of menu items to display (Ant Design Menu items format) */
  menuItems?: MenuProps["items"]
  /** Whether the context menu should be enabled */
  isEnabled?: boolean
  /** Additional CSS selectors to check when determining if click is outside */
  additionalCloseSelectors?: string[]
  /** Base CSS class name for the context menu */
  baseClassName?: string
  /** Offset from cursor position (default: 10px to the right) */
  offsetX?: number
  /** Offset from cursor position (default: 0px vertically) */
  offsetY?: number
}

/**
 * Return type for useContextMenu hook
 */
export interface UseContextMenuReturn {
  /** Whether the context menu is currently visible */
  isVisible: boolean
  /** Current position of the context menu */
  position: ContextMenuPosition
  /** Handler for context menu events */
  handleContextMenu: (e: React.MouseEvent) => void
  /** Handler to close the context menu */
  handleClose: () => void
  /** Rendered context menu component */
  ContextMenu: React.ReactNode
}

/**
 * Custom hook for managing context menu functionality
 */
const useContextMenu = ({
  menuItems = [],
  isEnabled = true,
  additionalCloseSelectors = [],
  baseClassName = "context-menu",
  offsetX = 15,
  offsetY = -5,
}: UseContextMenuOptions): UseContextMenuReturn => {
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [position, setPosition] = useState<ContextMenuPosition>({ x: 0, y: 0 })

  /**
   * Handles right-click context menu events
   */
  const handleContextMenu = useCallback(
    (e: React.MouseEvent): void => {
      if (!isEnabled) return
      e.preventDefault()
      e.stopPropagation()

      // Get the mouse position directly from the native event
      // This bypasses any React synthetic event coordinate issues
      const nativeEvent = e.nativeEvent
      let mouseX = nativeEvent.clientX
      let mouseY = nativeEvent.clientY

      // Alternative: use page coordinates and convert
      if (nativeEvent.pageX && nativeEvent.pageY) {
        mouseX = nativeEvent.pageX - window.pageXOffset
        mouseY = nativeEvent.pageY - window.pageYOffset
      }

      // Apply offset
      let x = mouseX + offsetX
      let y = mouseY + offsetY

      // Basic boundary checking
      const menuWidth = 180
      const menuHeight = menuItems ? menuItems.length * 36 + 20 : 120

      // Check boundaries and adjust if needed
      if (x + menuWidth > window.innerWidth - 10) {
        x = mouseX - menuWidth - 5
      }
      if (y + menuHeight > window.innerHeight - 10) {
        y = mouseY - menuHeight - 5
      }

      // Ensure menu stays on screen
      x = Math.max(10, Math.min(x, window.innerWidth - menuWidth - 10))
      y = Math.max(10, Math.min(y, window.innerHeight - menuHeight - 10))

      setPosition({ x, y })
      setIsVisible(true)
    },
    [isEnabled, offsetX, offsetY, menuItems]
  )

  /**
   * Closes the context menu
   */
  const handleClose = useCallback((): void => {
    setIsVisible(false)
  }, [])

  // Handle clicks outside the context menu
  useEffect(() => {
    const handleClickOutside = (e: Event): void => {
      if (!isVisible) return

      const target = e.target as Element
      const menuSelectors = [`.${baseClassName}`, ...additionalCloseSelectors]
      const clickedInsideMenu = menuSelectors.some((selector) =>
        target.closest(selector)
      )

      if (!clickedInsideMenu) {
        handleClose()
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [isVisible, handleClose, baseClassName, additionalCloseSelectors])

  // Rendered context menu component
  const ContextMenu = useMemo((): React.ReactNode => {
    if (!isVisible) return null

    const menuElement = (
      <div
        className={baseClassName}
        style={{
          position: "fixed",
          top: position.y,
          left: position.x,
          zIndex: 1000,
          backgroundColor: "white",
          border: "1px solid #ddd",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          minWidth: "160px",
        }}
      >
        <Menu items={menuItems} />
      </div>
    )

    // Render the context menu as a portal to document.body
    // This ensures it's not affected by any parent transforms or scaling
    return createPortal(menuElement, document.body)
  }, [isVisible, position, menuItems, baseClassName])

  return {
    isVisible,
    position,
    handleContextMenu,
    handleClose,
    ContextMenu,
  }
}

export default useContextMenu
